import { z } from "zod";

/** One quota field: integer ≥0 or -1 (unlimited). */
const quotaField = z.coerce
  .number()
  .refine((n) => Number.isFinite(n) && (n === -1 || n >= 0), "Must be 0+ or -1 for unlimited");

const planFields = {
  name: z.string().trim().min(1, "Plan name is required"),
  description: z.string().trim().min(1, "Description is required"),
  status: z.string().min(1, "Status is required"),
  chatDailyLimit: quotaField,
  chatWeeklyLimit: quotaField,
  chatMonthlyLimit: quotaField,
  mailDailyLimit: quotaField,
  mailWeeklyLimit: quotaField,
  mailMonthlyLimit: quotaField,
};

const planShape = z.object(planFields);

function refineQuotas(
  data: {
    chatDailyLimit: number;
    chatWeeklyLimit: number;
    chatMonthlyLimit: number;
    mailDailyLimit: number;
    mailWeeklyLimit: number;
    mailMonthlyLimit: number;
  },
  ctx: z.RefinementCtx
) {
  const quotaValues = [
    data.chatDailyLimit,
    data.chatWeeklyLimit,
    data.chatMonthlyLimit,
    data.mailDailyLimit,
    data.mailWeeklyLimit,
    data.mailMonthlyLimit,
  ];
  const hasInvalid = quotaValues.some(
    (value) => !Number.isFinite(value) || (value < 0 && value !== -1)
  );
  if (hasInvalid) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Quota values must be numeric. For unlimited use -1, otherwise 0 or positive.",
    });
  }
  const hasAtLeastOne = quotaValues.some((value) => value > 0 || value === -1);
  if (!hasAtLeastOne) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "At least one quota must be >0 or -1 (unlimited).",
    });
  }
}

export const planCreateFormSchema = planShape.superRefine((val, ctx) => refineQuotas(val, ctx));

export const planEditFormSchema = planShape
  .extend({
    id: z.string().trim().min(1, "Record id is required"),
  })
  .superRefine((val, ctx) => {
    refineQuotas(
      {
        chatDailyLimit: val.chatDailyLimit,
        chatWeeklyLimit: val.chatWeeklyLimit,
        chatMonthlyLimit: val.chatMonthlyLimit,
        mailDailyLimit: val.mailDailyLimit,
        mailWeeklyLimit: val.mailWeeklyLimit,
        mailMonthlyLimit: val.mailMonthlyLimit,
      },
      ctx
    );
  });
